import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import type { SafetyStockSummaryRow } from '@/services/safetyStockService';

interface Props {
  summary: SafetyStockSummaryRow | null;
  errorsTotal?: number;
}

/** Known engine warning codes → i18n key under safetyStock.summary.alerts.codes.* */
const KNOWN_WARNING_CODES = new Set([
  'FIXED_LEAD_TIME_FALLBACK',
  'INSUFFICIENT_POSITIVE_DEMAND',
  'FALLBACK_APPLIED',
  'NO_DEMAND_HISTORY',
  'LIMITED_DEMAND_HISTORY',
  'INSUFFICIENT_DEMAND_HISTORY',
  'INSUFFICIENT_LEAD_TIME_HISTORY',
  'OUTLIER_REMOVED',
  'OUTLIER_CAPPED',
  'METHOD_FALLBACK',
  'SERVICE_LEVEL_DEFAULT',
]);

function warningLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  code: string,
  count: number,
): string {
  const key = `safetyStock.summary.alerts.codes.${code}`;
  if (KNOWN_WARNING_CODES.has(code)) {
    return t(key, { count });
  }
  return t('safetyStock.summary.alerts.codes.UNKNOWN', { count });
}

export function AlertsSection({ summary, errorsTotal = 0 }: Props) {
  const { t } = useTranslation();
  if (!summary) return null;

  const warnings = Object.entries(summary.warning_mix || {}).sort(
    (a, b) => b[1] - a[1],
  );
  const unvalued = Math.max(0, (summary.items || 0) - (summary.valued_items || 0));
  const failed = summary.items_failed || errorsTotal || 0;

  if (!warnings.length && unvalued === 0 && failed === 0) {
    return (
      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        {t('safetyStock.summary.alerts.none')}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-amber-200 bg-white p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <h2 className="text-base font-semibold text-gray-900">
          {t('safetyStock.summary.alerts.title')}
        </h2>
      </div>
      <p className="mb-3 text-sm text-gray-500">{t('safetyStock.summary.alerts.help')}</p>
      <ul className="space-y-2 text-sm text-gray-700">
        {unvalued > 0 ? (
          <li>
            {t('safetyStock.summary.alerts.unvalued', {
              count: unvalued,
              total: summary.items || 0,
            })}
          </li>
        ) : null}
        {failed > 0 ? (
          <li>
            {t('safetyStock.summary.alerts.failed', { count: failed })}
          </li>
        ) : null}
        {warnings.map(([code, count]) => (
          <li key={code}>{warningLabel(t, code, count)}</li>
        ))}
      </ul>
      {warnings.length > 0 ? (
        <p className="mt-3 text-xs text-gray-500">
          <Link
            to="/safety-stock/productos?has_warnings=true"
            className="font-medium text-stocker-primary hover:underline"
          >
            {t('safetyStock.summary.alerts.viewProducts')}
          </Link>
        </p>
      ) : null}
    </section>
  );
}
